import { useMemo } from 'react'

import { Button, Flex, Span } from '@chakra-ui/react'
import { useTranslation } from '@infraloop/i18n'

import { ProjectTab } from '../utils'

const ProjectTabs = ({
  activeTab,
  ownedCount,
  invitedCount,
  onSelect,
  hasBaseAccess,
}: {
  /** Currently selected tab. */
  activeTab: ProjectTab
  /** Number of owned projects (Created tab badge). */
  ownedCount: number
  /** Number of invited projects (Invited tab badge). */
  invitedCount: number
  /** True when the viewer has a base subscription, which is required to view Created projects. */
  hasBaseAccess: boolean
  /** Called when a tab is selected. */
  onSelect: (tab: ProjectTab) => void
}) => {
  // Hooks
  const { t } = useTranslation()

  // Derived state
  const tabs = useMemo(
    () => [
      { tab: ProjectTab.Owned, label: t('hatsuly.tabs.owned'), count: ownedCount },
      { tab: ProjectTab.Invited, label: t('hatsuly.tabs.invited'), count: invitedCount },
    ],
    [ownedCount, invitedCount, t],
  )

  // If the user doesn't have a base subscription, don't show the tabs at all.
  // It'll just be a single table of shared projects anyway.
  if (!hasBaseAccess) return null

  return (
    <Flex mt="34px" borderBottom="1px solid" borderColor="gray.200">
      {tabs.map(({ tab, label, count }) => {
        const isActive = tab === activeTab
        return (
          <Button
            key={tab}
            variant="plain"
            h="auto"
            minW="0"
            px="0"
            py="12px"
            gap="8px"
            mr="26px"
            mb="-1px"
            borderRadius="0"
            borderWidth="0"
            borderBottomWidth="2px"
            borderStyle="solid"
            borderColor={isActive ? 'primary.500' : 'transparent'}
            fontSize="14px"
            fontWeight={isActive ? 'bold' : 'medium'}
            color={isActive ? 'gray.800' : 'gray.500'}
            cursor="pointer"
            _hover={{ color: 'gray.800' }}
            onClick={() => onSelect(tab)}
            data-testid={`tab-${tab}`}
          >
            {label}
            <Span
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              minW="20px"
              h="20px"
              px="6px"
              borderRadius="full"
              fontSize="12px"
              fontWeight="semibold"
              bg={isActive ? 'primary.50' : 'gray.100'}
              color={isActive ? 'primary.700' : 'gray.500'}
            >
              {count}
            </Span>
          </Button>
        )
      })}
    </Flex>
  )
}

export default ProjectTabs
